import { useContext } from "react";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import {
  BulkAlertPreferencesResponse,
  MasterPreference,
  Preference,
  UpdatedPreference,
} from "../../pages/preferences/types";

const usePreferencesApi = () => {
  const axios = useContext(ApiContext);
  const { PREFERENCES_SERVICE_URL: preferencesServiceUrl } =
    useContext(ConfigContext);
  // @ts-ignore
  return {
    fetchMasterPreferences: async (): Promise<MasterPreference[]> =>
      axios
        .getWithRetries<{
          preferences: MasterPreference[];
        }>(`${preferencesServiceUrl}/preferences/v1/master`)
        .then(({ data: { preferences } }) => preferences),

    fetchAccountPreferences: async (
      dealerAccountId: string
    ): Promise<Preference[]> =>
      axios
        .getWithRetries<{ preferences: Preference[] }>(
          `${preferencesServiceUrl}/preferences/v1/account/${dealerAccountId}`
        )
        .then(({ data: { preferences } }) => preferences),

    saveAccountPreferences:
      (
        dealerAccountId: string | undefined,
        postSave: (delta: Preference[]) => void
      ): ((delta: Preference[]) => Promise<void>) =>
      (delta: Preference[]): Promise<void> => {
        if (!dealerAccountId) {
          return Promise.reject("Dealer account id is missing.");
        }
        return axios
          .postWithRetries(
            `${preferencesServiceUrl}/preferences/v1/account/${dealerAccountId}`,
            { preferences: delta }
          )
          .then(() => postSave(delta));
      },
    saveUserPreferences:
      (
        dealerAccountId: string | undefined,
        userId: string,
        postSave: (delta: UpdatedPreference[]) => void
      ): ((delta: UpdatedPreference[]) => Promise<void>) =>
      (delta: UpdatedPreference[]): Promise<void> =>
        axios
          .postWithRetries(
            dealerAccountId
              ? `${preferencesServiceUrl}/preferences/v1/user/${userId}?accountId=${dealerAccountId}`
              : `${preferencesServiceUrl}/preferences/v1/user/${userId}`,
            { preferences: delta }
          )
          .then(() => postSave(delta)),

    fetchUserPreferences: (
      userXid: string,
      dealerAccountId: string | undefined
    ): Promise<Preference[]> =>
      axios
        .getWithRetries<{ preferences: Preference[] }>(
          dealerAccountId
            ? `${preferencesServiceUrl}/preferences/v1/user/${userXid}?accountId=${dealerAccountId}`
            : `${preferencesServiceUrl}/preferences/v1/user/${userXid}`
        )
        .then(({ data: { preferences } }) => preferences),

    fetchBulkAlertPreferences: async (
      userIds: string[],
      accountId: string,
      preferenceCategory: string,
      eventType: string = "NOTIFICATION",
      accessToken?: string
    ): Promise<BulkAlertPreferencesResponse> =>
      axios
        .postWithRetries(
          `${preferencesServiceUrl}/preferences/v1/alerts-and-notifications`,
          {
            userId: userIds,
            accountId,
            eventType,
            preferenceCategory,
          },
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        )
        .then(({ data }) => data),
  };
};
export default usePreferencesApi;
